import { useState, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import Sidebar from '../components/Sidebar';
import FileUploadZone from '../components/FileUploadZone';
import { apiFetch, API_BASE, ApiError } from '../lib/api';
import { HelpPopover } from '../components/help';
import DeliveryModePicker, {
  DEFAULT_CHECKPOINTS,
  defaultCheckpointKeys,
  checkpointKey,
} from '../components/DeliveryModePicker';
import GitHubRepoSetupSection, { type GitHubSetupPayload } from '../components/GitHubRepoSetupSection';

// ── Types ──────────────────────────────────────────────────────────────────

interface ImportResult {
  filename: string;
  artefactId: string | null;
  artefactType: string | null;
  phase: string | null;
  confidence: number | null;
  status: 'IMPORTED' | 'NEEDS_REVIEW' | 'FAILED';
  reason?: string;
}

interface ImportResponse {
  results: ImportResult[];
  summary: { total: number; imported: number; needsReview: number; failed: number };
}

type PriorPhaseAction = 'import' | 'waive' | 'empty';

interface PriorPhaseState {
  phase: string;
  label: string;
  action: PriorPhaseAction;
  waiverReason: string;
}

// ── Constants ──────────────────────────────────────────────────────────────

const ARTEFACT_TYPES = [
  { value: 'prd', label: 'PRD' },
  { value: 'sad', label: 'SAD' },
  { value: 'adr', label: 'ADR' },
  { value: 'erd', label: 'ERD' },
  { value: 'user_stories', label: 'User Stories' },
  { value: 'sprint_plan', label: 'Sprint Plan' },
  { value: 'test_plan', label: 'Test Plan' },
  { value: 'deployment_runbook', label: 'Deployment Runbook' },
  { value: 'retro_report', label: 'Retro Report' },
  { value: 'analysis_report', label: 'Analysis Report' },
] as const;

// `number` is the URL-safe phase wire value (Phase 2.5 → 25 to satisfy the
// digits-only param regex). `order` is the SDLC sequence and is what
// prior-phase filtering uses — they diverge for fractional phases like 2.5.
const PHASES = [
  { value: 'phase_1_requirements',    label: 'Phase 1 — Requirements',    number: 1,  order: 1.0 },
  { value: 'phase_2_architecture',    label: 'Phase 2 — Architecture',    number: 2,  order: 2.0 },
  { value: 'phase_2_5_ux_design',     label: 'Phase 2.5 — UX Design',     number: 25, order: 2.5 },
  { value: 'phase_3_sprint_planning', label: 'Phase 3 — Sprint Planning', number: 3,  order: 3.0 },
  { value: 'phase_4_development',     label: 'Phase 4 — Development',     number: 4,  order: 4.0 },
  { value: 'phase_5_code_review',     label: 'Phase 5 — Code Review',     number: 5,  order: 5.0 },
  { value: 'phase_6_testing',         label: 'Phase 6 — Testing',         number: 6,  order: 6.0 },
  { value: 'phase_7_deployment',      label: 'Phase 7 — Deployment',      number: 7,  order: 7.0 },
] as const;

const STEP_LABELS = ['Project Details', 'Prior Phases', 'Upload Documents', 'Review Classifications', 'Complete'];

// MODE.B2 (#1175) R3 MEDIUM-2 fold-in — shared initial value for the
// brownfield wizard's githubPayload state. Used both at mount AND on
// Step-2 Back to keep parent state in lock-step with the section's
// internal field state (which resets on unmount/remount of Step 2).
const INITIAL_GITHUB_PAYLOAD = { mode: 'link_existing' as const, owner: '', repo: '' };

// MODE.B4 (#1177) — Domain Pack options, inline-duplicated from
// NewProjectModal:34-39 per the codebase idiom (PHASES + the style
// consts are likewise locally duplicated between these two files).
// Kept local to avoid touching NewProjectModal, keeping #1238's
// Domain<->GitHub reorder on a clean base. Mirrors the canonical types
// in apps/api/src/db/schema/domain-packs.ts.
const DOMAIN_PACKS = [
  { value: 'generic',    label: 'Generic — no industry-specific context' },
  { value: 'healthcare', label: 'Healthcare — HIPAA / NABH / WCAG 2.1 AA' },
  { value: 'bfsi',       label: 'BFSI — RBI / SEBI cyber-resilience' },
] as const;
type DomainPackValue = (typeof DOMAIN_PACKS)[number]['value'];

// ── Styles ─────────────────────────────────────────────────────────────────

const inputStyle: React.CSSProperties = {
  display: 'block',
  width: '100%',
  height: 44,
  background: 'var(--surface-alt)',
  border: '1px solid var(--border)',
  borderRadius: 'var(--radius-sm)',
  padding: '0 14px',
  fontFamily: 'var(--font-body)',
  fontSize: 14,
  color: 'var(--text)',
  outline: 'none',
  boxSizing: 'border-box',
};

const selectStyle: React.CSSProperties = {
  ...inputStyle,
  appearance: 'none',
  backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='12' viewBox='0 0 12 12'%3E%3Cpath d='M3 4.5L6 7.5L9 4.5' stroke='%236B7280' stroke-width='1.5' fill='none'/%3E%3C/svg%3E")`,
  backgroundRepeat: 'no-repeat',
  backgroundPosition: 'right 14px center',
  paddingRight: 36,
};

function confidenceColor(c: number | null): { bg: string; color: string; label: string } {
  if (c === null) return { bg: 'rgba(107,114,128,0.10)', color: 'var(--text-secondary)', label: 'N/A' };
  if (c >= 0.8) return { bg: 'rgba(16,185,129,0.12)', color: 'var(--success)', label: 'High' };
  if (c >= 0.5) return { bg: 'rgba(245,158,11,0.12)', color: 'var(--warning)', label: 'Medium' };
  return { bg: 'rgba(239,68,68,0.10)', color: 'var(--error)', label: 'Low' };
}

// ── Component ──────────────────────────────────────────────────────────────

export default function BrownfieldOnboardingPage() {
  const navigate = useNavigate();
  const [step, setStep] = useState(0);

  // MODE.Cfix.A1 — the DeliveryModePicker is shown to every tenant (the
  // MODE.A2 Trial-hide and its useMyTenant billing lookup are removed).
  // Governance is a universal, billing-independent choice; the submit
  // handler always sends the explicit deliveryMode so a Governed pick
  // reaches the API.

  // Step 0 — Project details + phase selection
  const [name, setName] = useState('');
  const [productArea, setProductArea] = useState('');
  const [startingPhase, setStartingPhase] = useState('phase_1_requirements');

  // MODE.B1 (#1174) — Delivery Mode + checkpoints. Default 'governed'
  // matches the projects.delivery_mode column default and the Indium SI
  // use case. The Set is pre-populated with all three default
  // checkpoint keys so toggling Accelerate immediately shows the
  // canonical Phase 1/5/7 EXIT profile, matching NewProjectModal's UX.
  // MODE.Cfix.A1 — the POST body now ALWAYS sends deliveryMode explicitly
  // (both 'governed' and 'accelerate'); `checkpoints` is still omitted for
  // governed. The column default is identical, but the explicit send means
  // a future column-default change cannot silently override the user's pick.
  const [deliveryMode, setDeliveryMode] = useState<'governed' | 'accelerate'>('governed');
  const [selectedCheckpoints, setSelectedCheckpoints] = useState<Set<string>>(
    () => defaultCheckpointKeys(),
  );

  // Step 1 — Prior phase handling
  const [priorPhases, setPriorPhases] = useState<PriorPhaseState[]>([]);
  // MODE.B1 R1 fold-in — tracks which startingPhase produced the
  // current priorPhases array so Back → Re-Next doesn't clobber the
  // user's typed waiver reasons. Pre-existing bug surfaced by R1
  // sibling-code inspection of MODE.B1; fix is minimal and lives in
  // the same function MODE.B1 touched for the min-checkpoint guard.
  const priorPhasesBuiltForRef = useRef<string | null>(null);

  // Step 2 — File upload + GitHub binding
  const [files, setFiles] = useState<File[]>([]);
  // MODE.B2 (#1175) — GitHub binding payload, embedded in POST /projects.
  //
  // Pre-initialised to match the section's initialMode='link_existing'
  // pre-selection (brownfield user is onboarding existing work; the
  // hint is grounded in the user's context). Empty owner/repo strings
  // mirror the section's emit shape for that mode until the user
  // types — `submittable` derives validity from the same fields.
  //
  // POST /projects body embeds this as `github: payload`. The Shape 2
  // rollback at routes/projects.ts:680-755 handles ADAPTER_FAILURE
  // atomically (delete project + audit_logs FK-race close); brownfield
  // gets the same atomicity that NewProjectModal does, without inventing
  // its own multi-resource cleanup. AC line "Wires to POST /:id/github-
  // repo" was inherited wording from the kickoff prompt; load-bearing
  // requirement is the rollback line per adjudication D.
  const [githubPayload, setGithubPayload] = useState<GitHubSetupPayload | null>(INITIAL_GITHUB_PAYLOAD);
  // Mirror NewProjectModal:99-106 — modal owns the controlled error
  // surface for bind-step failures so the section's [Connect GitHub →]
  // / [Retry] CTAs render co-located with the GitHub controls. Cleared
  // by onRetry (operator-explicit) or onModeChange (V4 — user toggles
  // mode signalling recovery intent).
  const [githubErrorMsg, setGithubErrorMsg] = useState<string | null>(null);
  const [githubErrorNextAction, setGithubErrorNextAction] = useState<
    'reconnect_github' | 'check_github_status' | null
  >(null);
  // R1+R3 LOW fold-in — wrap in useCallback so goToReview's dep array
  // can include it without re-creating every render. State setters from
  // useState are stable refs, so the dep array is empty.
  const clearGithubError = useCallback(() => {
    setGithubErrorMsg(null);
    setGithubErrorNextAction(null);
  }, []);
  // R3 MEDIUM-2 fold-in — reset the payload when the user goes Back
  // from Step 2. The section unmounts on `step !== 2` (renderStep2 is
  // conditionally rendered) and its internal field state resets on
  // re-entry. Without resetting the parent's payload here, the parent
  // keeps the stale pre-Back values while the section shows empty
  // fields → user could submit stale data they no longer see.
  const resetGithubPayload = useCallback(() => {
    setGithubPayload(INITIAL_GITHUB_PAYLOAD);
  }, []);

  // MODE.B4 (#1177) — Domain Pack selection, rendered canonical-LAST in
  // Step 2 (after the GitHub section) per B5's canonical-ordering memo.
  // Default 'generic' (the runtime fallback); applied via PATCH
  // /projects/:id/domain-pack after create in goToReview, mirroring
  // NewProjectModal. Skipped for Generic, non-fatal on failure.
  // Intentionally NOT reset on Step-2 Back (unlike githubPayload): the
  // <select> is driven directly by this parent state, so there is no
  // parent/child divergence to reconcile — persisting the user's choice
  // across Back matches name/productArea/startingPhase.
  const [domainPack, setDomainPack] = useState<DomainPackValue>('generic');

  // Step 3 — Classification review
  const [results, setResults] = useState<ImportResult[]>([]);
  const [summary, setSummary] = useState<ImportResponse['summary'] | null>(null);
  const [projectId, setProjectId] = useState<string | null>(null);

  // Shared
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  // ── Helpers ─────────────────────────────────────────────────────────────

  const selectedPhaseOrder = PHASES.find(p => p.value === startingPhase)?.order ?? 1;
  const hasPriorPhases = selectedPhaseOrder > 1;

  // MODE.B1 (#1174) — symmetric guard with NewProjectModal:118. When
  // Accelerate is selected with zero checkpoints, the picker shows a
  // warning AND the wizard's Next button on Step 0 disables. The
  // server-side .refine() in createProjectSchema is the defence-in-
  // depth backstop for non-UI clients.
  const minCheckpointGuard =
    deliveryMode === 'accelerate' && selectedCheckpoints.size === 0;

  // MODE.B2 (#1175) — client-side guard for incomplete link_existing.
  // Section emits a payload with empty owner/repo strings if the user
  // picks link_existing but doesn't fill the fields. Backend
  // z.string().min(1) rejects with 400 (ZodError), not the structured
  // 422 nextAction shape. The user has uploaded a file by the time
  // they hit Upload & Classify, so a backend bounce here costs more
  // than a client-side disable. Defence-in-depth — backend validation
  // still authoritative for non-UI clients.
  const githubLinkIncomplete =
    githubPayload?.mode === 'link_existing' &&
    (!githubPayload.owner?.trim() || !githubPayload.repo?.trim());

  const buildPriorPhases = (phaseValue: string) => {
    const phaseOrder = PHASES.find(p => p.value === phaseValue)?.order ?? 1;
    if (phaseOrder <= 1) return [];
    return PHASES.filter(p => p.order < phaseOrder).map(p => ({
      phase: p.value,
      label: p.label,
      action: 'empty' as PriorPhaseAction,
      waiverReason: '',
    }));
  };

  const updatePriorPhase = (index: number, update: Partial<PriorPhaseState>) => {
    setPriorPhases(prev => prev.map((p, i) => i === index ? { ...p, ...update } : p));
  };

  const canAdvanceFromPriorPhases = () => {
    return priorPhases.every(p => {
      if (p.action === 'waive') return p.waiverReason.trim().length >= 10;
      return true;
    });
  };

  // ── Step transitions ───────────────────────────────────────────────────

  const goToNextFromDetails = () => {
    if (!name.trim()) return;
    if (minCheckpointGuard) return;
    if (hasPriorPhases) {
      // Only rebuild priorPhases when the user changed startingPhase
      // (or this is the first entry to Step 1). Re-Next after Back —
      // with the same startingPhase — preserves user-entered waiver
      // reasons and action selections. Phase change still rebuilds
      // because the prior-phase list itself has changed.
      if (priorPhasesBuiltForRef.current !== startingPhase) {
        setPriorPhases(buildPriorPhases(startingPhase));
        priorPhasesBuiltForRef.current = startingPhase;
      }
      setStep(1);
    } else {
      // Skip prior phases step. R1 round-2 audit surfaced a phantom-
      // waiver path here: if the user previously visited Step 1 at a
      // higher startingPhase and waived some phases, then changed
      // back to Phase 1 (hasPriorPhases=false), the prior priorPhases
      // array still lives in state. goToReview reads priorPhases
      // directly and would POST those phantom waivers under the
      // current Phase-1 project. Clear state + ref so the bulk-
      // waivers call is skipped on this path.
      if (priorPhases.length > 0) {
        setPriorPhases([]);
      }
      priorPhasesBuiltForRef.current = null;
      setStep(2);
    }
  };

  const goToUpload = () => {
    if (hasPriorPhases && !canAdvanceFromPriorPhases()) return;
    setStep(2);
  };

  const goToReview = useCallback(async () => {
    if (files.length === 0) return;
    if (githubLinkIncomplete) return;
    // R3 round-1 HIGH-2 + round-2 MEDIUM fold-in — block re-entry when
    // a project has already been created in this wizard session.
    // Covers two scenarios:
    //   * Ghost projectId after partial POST failure → retry would re-
    //     POST /projects and create a duplicate.
    //   * Step-3 Back returns the user to Step 2 with projectId already
    //     set → re-click would also create a duplicate.
    // Round-3 audit must verify the guard doesn't break the legitimate
    // first-time POST flow (projectId === null at entry).
    if (projectId) return;
    setLoading(true);
    setError('');
    // MODE.B2 (#1175) — clear any prior GitHub bind-step error before re-submit.
    clearGithubError();

    // R1 MEDIUM-1 fold-in — discriminator for the catch block. The
    // structured GitHub nextAction error routing should only fire when
    // POST /projects itself fails; if /projects succeeded and a later
    // POST (/phase-waivers/bulk or /artefacts/import) throws with a
    // coincidentally-matching nextAction field, the GitHub bind step
    // has ALREADY succeeded — routing into the section's error props
    // would misleadingly imply the GitHub step still needs attention.
    let projectCreated = false;

    // MODE.B1 (#1174) — Accelerate-only checkpoints payload. Mirrors
    // NewProjectModal:150-154: keep the Set-based UI state, translate
    // to the array shape the backend schema expects, and only include
    // the field when the user has chosen Accelerate. Governed projects
    // omit the field entirely (column default fires).
    const checkpointsPayload = deliveryMode === 'accelerate'
      ? DEFAULT_CHECKPOINTS
          .filter((c) => selectedCheckpoints.has(checkpointKey(c)))
          .map((c) => ({ phase: c.phase, gateType: c.gateType }))
      : undefined;

    try {
      // 1. Create the project with starting phase
      const project = await apiFetch<{ id: string }>('/projects', {
        method: 'POST',
        body: JSON.stringify({
          name: name.trim(),
          ...(productArea.trim() ? { productArea: productArea.trim() } : {}),
          currentPhase: startingPhase,
          // MODE.B6 (#1179): tenantId + createdBy omitted — the server
          // resolves both from the auth context (membership + userId) and
          // discards any body-supplied values (routes/projects.ts:494,527-529).
          // MODE.Cfix.A1 — always send the explicit deliveryMode (both
          // 'governed' and 'accelerate'), mirroring NewProjectModal. The
          // picker is shown to every tenant now, so a Governed pick must
          // reach the API; the backend no longer infers a Trial→Accelerate
          // default for an omitted field.
          deliveryMode,
          ...(checkpointsPayload && { checkpoints: checkpointsPayload }),
          // MODE.B2 (#1175): embed github payload when the user has
          // chosen a mode. Omission preserves NewProjectModal's
          // seeder/test backwards-compat (BootstrapAgent's existing
          // tenantInfra-defaulted CREATE_REPO flow). Truthy spread
          // guard prevents `github: undefined` from leaking into the
          // JSON body where Zod would reject it.
          ...(githubPayload ? { github: githubPayload } : {}),
        }),
      });
      projectCreated = true;
      setProjectId(project.id);

      // MODE.B4 (#1177) — apply the selected domain pack after create,
      // mirroring NewProjectModal:163-175. Skipped for Generic (the
      // runtime default — avoids a redundant write + audit-log row).
      // Non-fatal: the project already exists, so a pack failure must
      // not abort the import flow. AC#3's "flows into POST body" resolves
      // to the intent "selected pack applied to the project" —
      // createProjectSchema has no domainPackId field (Zod strips it),
      // so the pattern is a PATCH after create, not a POST-body field
      // (no API change, per the issue's UI-only Technical Notes).
      if (domainPack !== 'generic') {
        try {
          await apiFetch(`/projects/${project.id}/domain-pack`, {
            method: 'PATCH',
            body: JSON.stringify({ packType: domainPack }),
          });
        } catch (packErr) {
          console.warn('[BrownfieldOnboardingPage] domain pack assignment failed:', packErr);
        }
      }

      // 2. Create phase waivers for waived prior phases
      const waiversToCreate = priorPhases.filter(p => p.action === 'waive');
      if (waiversToCreate.length > 0) {
        await apiFetch(`/projects/${project.id}/phase-waivers/bulk`, {
          method: 'POST',
          body: JSON.stringify({
            waivers: waiversToCreate.map(w => ({
              phase: w.phase,
              reason: w.waiverReason.trim(),
            })),
          }),
        });
      }

      // 3. Upload files to the import pipeline
      const formData = new FormData();
      files.forEach(f => formData.append('files', f));

      const token = localStorage.getItem('blueprint_token');
      const res = await fetch(`${API_BASE}/projects/${project.id}/artefacts/import`, {
        method: 'POST',
        headers: token ? { Authorization: `Bearer ${token}` } : {},
        credentials: 'include',
        body: formData,
      });

      if (!res.ok) {
        const body = await res.json().catch(() => ({ error: `HTTP ${res.status}` }));
        throw new Error((body as { error?: string }).error ?? `HTTP ${res.status}`);
      }

      const data = (await res.json()) as ImportResponse;
      setResults(data.results);
      setSummary(data.summary);
      setStep(3);
    } catch (err) {
      // MODE.B2 (#1175) — mirror NewProjectModal:169-184 EXACTLY (per
      // adjudication E). Route bind-step failures with the two
      // CTA-bearing nextAction codes into the section's controlled
      // error props so the [Connect GitHub →] / [Retry] CTAs render
      // co-located with the GitHub controls. The other 3 codes
      // (create_repo / check_repo_permissions / check_default_branch)
      // and all non-GitHub errors fall through to the form-level
      // slot — same as NewProjectModal. nextAction is the
      // discriminator per #1153 D8 uniform wire shape; the wizard
      // does NOT need to know which endpoint produced the 422.
      if (
        !projectCreated &&
        err instanceof ApiError &&
        (err.body.nextAction === 'reconnect_github' || err.body.nextAction === 'check_github_status')
      ) {
        setGithubErrorMsg(err.message);
        setGithubErrorNextAction(err.body.nextAction as 'reconnect_github' | 'check_github_status');
      } else {
        setError(err instanceof Error ? err.message : 'Import failed');
      }
    } finally {
      setLoading(false);
    }
  }, [files, name, productArea, startingPhase, priorPhases, deliveryMode, selectedCheckpoints, domainPack, githubPayload, githubLinkIncomplete, clearGithubError, projectId]);

  const goToComplete = () => {
    if (!projectId) return;
    setStep(4);
  };

  // ── Render helpers ─────────────────────────────────────────────────────

  const renderStepper = () => {
    // If no prior phases, skip step 1 in display
    const displaySteps = hasPriorPhases
      ? STEP_LABELS
      : STEP_LABELS.filter((_, i) => i !== 1);
    const displayStep = hasPriorPhases
      ? step
      : step > 0 ? step - 1 : step;

    return (
      <div className="flex items-center justify-center mb-8" style={{ gap: 8 }}>
        {displaySteps.map((label, i) => (
          <div key={label} className="flex items-center" style={{ gap: 8 }}>
            <div className="flex items-center" style={{ gap: 6 }}>
              <div
                style={{
                  width: 28,
                  height: 28,
                  borderRadius: '50%',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontSize: 12,
                  fontWeight: 600,
                  fontFamily: 'var(--font-display)',
                  background: i <= displayStep ? 'var(--primary)' : 'var(--surface-alt)',
                  color: i <= displayStep ? 'white' : 'var(--text-secondary)',
                  border: i <= displayStep ? 'none' : '1px solid var(--border)',
                  transition: 'all 200ms',
                }}
              >
                {i < displayStep ? '\u2713' : i + 1}
              </div>
              <span
                style={{
                  fontFamily: 'var(--font-body)',
                  fontSize: 13,
                  fontWeight: i === displayStep ? 600 : 400,
                  color: i <= displayStep ? 'var(--text)' : 'var(--text-secondary)',
                }}
              >
                {label}
              </span>
            </div>
            {i < displaySteps.length - 1 && (
              <div
                style={{
                  width: 32,
                  height: 2,
                  background: i < displayStep ? 'var(--primary)' : 'var(--border)',
                  transition: 'background 200ms',
                }}
              />
            )}
          </div>
        ))}
      </div>
    );
  };

  const renderStep0 = () => (
    <div style={{ maxWidth: 480, margin: '0 auto' }}>
      <h2 style={{ fontFamily: 'var(--font-display)', fontSize: 20, fontWeight: 700, color: 'var(--text)', marginBottom: 4 }}>
        Project Details
      </h2>
      <p style={{ fontFamily: 'var(--font-body)', fontSize: 14, color: 'var(--text-secondary)', marginBottom: 24 }}>
        Tell us about your existing project so we can organise your imported artefacts.
      </p>

      <div style={{ marginBottom: 20 }}>
        <label style={{ display: 'block', fontFamily: 'var(--font-body)', fontSize: 13, fontWeight: 500, color: 'var(--text)', marginBottom: 6 }}>
          Project Name <span style={{ color: 'var(--error)' }}>*</span>
        </label>
        <input
          type="text"
          value={name}
          onChange={e => setName(e.target.value)}
          placeholder="e.g. Legacy CRM Migration"
          style={inputStyle}
          onFocus={e => { e.currentTarget.style.borderColor = 'var(--primary)'; e.currentTarget.style.boxShadow = '0 0 0 3px rgba(124,58,237,0.12)'; }}
          onBlur={e => { e.currentTarget.style.borderColor = 'var(--border)'; e.currentTarget.style.boxShadow = 'none'; }}
        />
      </div>

      <div style={{ marginBottom: 20 }}>
        <label style={{ display: 'block', fontFamily: 'var(--font-body)', fontSize: 13, fontWeight: 500, color: 'var(--text)', marginBottom: 6 }}>
          Product Area{' '}
          <span style={{ color: 'var(--text-secondary)', fontWeight: 400 }}>(optional)</span>
        </label>
        <input
          type="text"
          value={productArea}
          onChange={e => setProductArea(e.target.value)}
          placeholder="e.g. Core Platform, Mobile App"
          style={inputStyle}
          onFocus={e => { e.currentTarget.style.borderColor = 'var(--primary)'; e.currentTarget.style.boxShadow = '0 0 0 3px rgba(124,58,237,0.12)'; }}
          onBlur={e => { e.currentTarget.style.borderColor = 'var(--border)'; e.currentTarget.style.boxShadow = 'none'; }}
        />
      </div>

      <div style={{ marginBottom: 24 }}>
        <label style={{ display: 'block', fontFamily: 'var(--font-body)', fontSize: 13, fontWeight: 500, color: 'var(--text)', marginBottom: 6 }}>
          Current Phase
        </label>
        <select
          value={startingPhase}
          onChange={e => setStartingPhase(e.target.value)}
          style={selectStyle}
          onFocus={e => { e.currentTarget.style.borderColor = 'var(--primary)'; e.currentTarget.style.boxShadow = '0 0 0 3px rgba(124,58,237,0.12)'; }}
          onBlur={e => { e.currentTarget.style.borderColor = 'var(--border)'; e.currentTarget.style.boxShadow = 'none'; }}
        >
          {PHASES.map(p => (
            <option key={p.value} value={p.value}>{p.label}</option>
          ))}
        </select>
        <p style={{ fontFamily: 'var(--font-body)', fontSize: 12, color: 'var(--text-secondary)', marginTop: 6 }}>
          Select the phase your project is currently in. Prior phases can be imported or waived.
        </p>
      </div>

      {/* MODE.B1 (#1174) — Delivery Mode picker as an inline Step-0
          section per adjudication B. Placed below Current Phase + above
          the Next button to keep the scroll position predictable.
          waivedPhases is derived from priorPhases so MODE.B3 can render
          the waived-phase note without the wizard needing further
          wiring; at Step 0 with no waiver decisions yet the list is
          empty, which is semantically correct.
          MODE.Cfix.A1 — shown to every tenant (the MODE.A2 Trial-hide is
          removed). Default is Governed; the submit handler always sends
          the explicit choice. Mode can be changed post-creation via PATCH. */}
      <div data-testid="delivery-mode-section-wrapper" style={{ marginBottom: 24 }}>
        <DeliveryModePicker
          value={deliveryMode}
          onChange={setDeliveryMode}
          checkpoints={selectedCheckpoints}
          onCheckpointsChange={setSelectedCheckpoints}
          waivedPhases={priorPhases.filter(p => p.action === 'waive').map(p => p.phase)}
        />
      </div>

      <div className="flex justify-end">
        <button
          type="button"
          disabled={!name.trim() || minCheckpointGuard}
          onClick={goToNextFromDetails}
          style={{
            padding: '10px 24px',
            fontFamily: 'var(--font-display)',
            fontSize: 14,
            fontWeight: 600,
            color: 'white',
            background: 'var(--primary)',
            border: 'none',
            borderRadius: 'var(--radius-sm)',
            cursor: (!name.trim() || minCheckpointGuard) ? 'not-allowed' : 'pointer',
            opacity: (!name.trim() || minCheckpointGuard) ? 0.5 : 1,
          }}
        >
          Next
        </button>
      </div>
    </div>
  );

  const renderStep1 = () => (
    <div style={{ maxWidth: 600, margin: '0 auto' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4 }}>
        <h2 style={{ fontFamily: 'var(--font-display)', fontSize: 20, fontWeight: 700, color: 'var(--text)', margin: 0 }}>
          Prior Phases
        </h2>
        <HelpPopover
          id="prior-phase-action"
          heading="Choosing prior-phase actions"
          body={
            <>
              <p style={{ margin: '0 0 8px' }}>
                For each phase before your starting phase, you decide how BluePrint should treat its artefacts.
              </p>
              <p style={{ margin: '0 0 6px' }}>
                <strong>Import</strong> — bring in existing documents in the next step. Use this when artefacts exist elsewhere and should be incorporated.
              </p>
              <p style={{ margin: '0 0 6px' }}>
                <strong>Waive</strong> — explicitly skip this phase. Requires a written reason and lands in the audit trail. Use this when you&apos;ve decided not to do this phase work at all.
              </p>
              <p style={{ margin: '0 0 8px' }}>
                <strong>Empty</strong> — leave the phase with no artefacts. The phase gate will block until satisfied later. Use this when you&apos;ll come back to it.
              </p>
              <p style={{ margin: 0 }}>
                If you&apos;re unsure, choose Import. It&apos;s the most flexible option and doesn&apos;t commit you to a downstream consequence.
              </p>
            </>
          }
        />
      </div>
      <p style={{ fontFamily: 'var(--font-body)', fontSize: 14, color: 'var(--text-secondary)', marginBottom: 24 }}>
        Your project starts at {PHASES.find(p => p.value === startingPhase)?.label}. For each prior phase, choose how to handle it.
      </p>

      <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
        {priorPhases.map((pp, idx) => (
          <div
            key={pp.phase}
            style={{
              border: '1px solid var(--border)',
              borderRadius: 'var(--radius-sm)',
              padding: 16,
              background: pp.action === 'waive' ? 'rgba(245,158,11,0.04)' : 'var(--surface)',
            }}
          >
            <div style={{ fontFamily: 'var(--font-display)', fontSize: 14, fontWeight: 600, color: 'var(--text)', marginBottom: 12 }}>
              {pp.label}
            </div>

            <div className="flex gap-2" style={{ flexWrap: 'wrap', marginBottom: pp.action === 'waive' ? 12 : 0 }}>
              {([
                { key: 'import' as const, label: 'Import artefacts', desc: 'Upload documents in the next step' },
                { key: 'waive' as const, label: 'Waive this phase', desc: 'Mark as explicitly skipped' },
                { key: 'empty' as const, label: 'Leave empty', desc: 'Gate will block until satisfied' },
              ]).map(opt => (
                <button
                  key={opt.key}
                  type="button"
                  onClick={() => updatePriorPhase(idx, { action: opt.key })}
                  title={opt.desc}
                  style={{
                    padding: '6px 14px',
                    fontFamily: 'var(--font-body)',
                    fontSize: 12,
                    fontWeight: pp.action === opt.key ? 600 : 400,
                    color: pp.action === opt.key ? 'white' : 'var(--text)',
                    background: pp.action === opt.key ? 'var(--primary)' : 'var(--surface-alt)',
                    border: pp.action === opt.key ? '1px solid var(--primary)' : '1px solid var(--border)',
                    borderRadius: 'var(--radius-sm)',
                    cursor: 'pointer',
                    transition: 'all 150ms',
                  }}
                >
                  {opt.label}
                </button>
              ))}
            </div>

            {pp.action === 'waive' && (
              <div>
                <label style={{ display: 'block', fontFamily: 'var(--font-body)', fontSize: 12, fontWeight: 500, color: 'var(--text)', marginBottom: 4 }}>
                  Waiver reason <span style={{ color: 'var(--error)' }}>*</span>
                  <span style={{ color: 'var(--text-secondary)', fontWeight: 400 }}> (min 10 characters)</span>
                </label>
                <textarea
                  value={pp.waiverReason}
                  onChange={e => updatePriorPhase(idx, { waiverReason: e.target.value })}
                  placeholder="e.g. Requirements already documented in Confluence and reviewed by stakeholders"
                  rows={2}
                  style={{
                    ...inputStyle,
                    height: 'auto',
                    padding: '10px 14px',
                    resize: 'vertical',
                    minHeight: 60,
                  }}
                  onFocus={e => { e.currentTarget.style.borderColor = 'var(--primary)'; e.currentTarget.style.boxShadow = '0 0 0 3px rgba(124,58,237,0.12)'; }}
                  onBlur={e => { e.currentTarget.style.borderColor = 'var(--border)'; e.currentTarget.style.boxShadow = 'none'; }}
                />
                {pp.waiverReason.trim().length > 0 && pp.waiverReason.trim().length < 10 && (
                  <p style={{ fontFamily: 'var(--font-body)', fontSize: 11, color: 'var(--error)', marginTop: 4 }}>
                    {10 - pp.waiverReason.trim().length} more character{10 - pp.waiverReason.trim().length !== 1 ? 's' : ''} needed
                  </p>
                )}
              </div>
            )}

            {pp.action === 'empty' && (
              <p style={{ fontFamily: 'var(--font-body)', fontSize: 11, color: 'var(--text-secondary)', marginTop: 4 }}>
                Phase gate will require artefacts before this phase can be marked complete.
              </p>
            )}

            {pp.action === 'import' && (
              <p style={{ fontFamily: 'var(--font-body)', fontSize: 11, color: 'var(--text-secondary)', marginTop: 4 }}>
                Upload documents for this phase in the next step.
              </p>
            )}
          </div>
        ))}
      </div>

      <div className="flex justify-between mt-8">
        <button
          type="button"
          onClick={() => setStep(0)}
          style={{
            padding: '10px 24px',
            fontFamily: 'var(--font-body)',
            fontSize: 14,
            fontWeight: 500,
            color: 'var(--text-secondary)',
            background: 'none',
            border: '1px solid var(--border)',
            borderRadius: 'var(--radius-sm)',
            cursor: 'pointer',
          }}
        >
          Back
        </button>
        <button
          type="button"
          disabled={!canAdvanceFromPriorPhases()}
          onClick={goToUpload}
          style={{
            padding: '10px 24px',
            fontFamily: 'var(--font-display)',
            fontSize: 14,
            fontWeight: 600,
            color: 'white',
            background: 'var(--primary)',
            border: 'none',
            borderRadius: 'var(--radius-sm)',
            cursor: !canAdvanceFromPriorPhases() ? 'not-allowed' : 'pointer',
            opacity: !canAdvanceFromPriorPhases() ? 0.5 : 1,
          }}
        >
          Next
        </button>
      </div>
    </div>
  );

  const renderStep2 = () => {
    // R3 round-3 STOP-AND-CONSULT resolution — Option C banner.
    //
    // When projectId is already set, the wizard cannot continue safely:
    // POST /projects has already succeeded, the re-entry guard would
    // block any re-submission, and POST /artefacts/import is not
    // currently idempotent (filed as P1 backend follow-on) so a
    // theoretical retry would create duplicate artefacts. Bouncing
    // the user out of the wizard to the project page is the
    // operationally correct resolution: project IS created, wizard
    // setup is complete-enough, the user finishes setup from the
    // project dashboard.
    //
    // This branch fires in two real scenarios:
    //   (1) User reached Step 3, clicked Back, lands here.
    //   (2) Partial-failure case: POST /projects succeeded, a later
    //       POST failed, user stays on Step 2 with an error banner.
    //       The Option C banner replaces the failure UI on the next
    //       click; user gets a clear next-action.
    if (projectId) {
      return (
        <div style={{ maxWidth: 560, margin: '0 auto' }} data-testid="project-already-created-banner">
          <h2 style={{ fontFamily: 'var(--font-display)', fontSize: 20, fontWeight: 700, color: 'var(--text)', marginBottom: 8 }}>
            Project &lsquo;{name.trim() || 'Untitled'}&rsquo; is already created
          </h2>
          <p style={{ fontFamily: 'var(--font-body)', fontSize: 14, color: 'var(--text-secondary)', marginBottom: 24 }}>
            You can continue setting it up from the project page.
          </p>
          <div className="flex justify-end">
            <button
              type="button"
              data-testid="project-already-created-go-to-project"
              onClick={() => navigate(`/projects/${projectId}`)}
              style={{
                padding: '10px 24px',
                fontFamily: 'var(--font-display)',
                fontSize: 14,
                fontWeight: 600,
                color: 'white',
                background: 'var(--primary)',
                border: 'none',
                borderRadius: 'var(--radius-sm)',
                cursor: 'pointer',
              }}
            >
              Go to Project
            </button>
          </div>
        </div>
      );
    }

    return (
    <div style={{ maxWidth: 560, margin: '0 auto' }}>
      <h2 style={{ fontFamily: 'var(--font-display)', fontSize: 20, fontWeight: 700, color: 'var(--text)', marginBottom: 4 }}>
        Upload Documents
      </h2>
      <p style={{ fontFamily: 'var(--font-body)', fontSize: 14, color: 'var(--text-secondary)', marginBottom: 24 }}>
        Upload your existing project documents. We&apos;ll classify each one automatically.
      </p>

      <FileUploadZone onFilesSelected={setFiles} accept=".md,.docx,.pdf" />

      {/* MODE.B2 (#1175) — GitHub binding section. Placed AFTER the
          FileUploadZone per adjudication B: the user uploads docs,
          then chooses their GitHub binding mode, then clicks Upload &
          Classify which fires a single POST /projects with `github`
          embedded. Inline section (not a new wizard step) avoids
          stepper bloat. The link_existing pre-selection is the
          brownfield UX hint per adjudication C. */}
      <div style={{ marginTop: 32 }}>
        <p
          data-testid="github-section-helper"
          style={{
            fontFamily: 'var(--font-body)',
            fontSize:   12,
            color:      'var(--text-secondary)',
            marginBottom: 8,
            lineHeight: 1.5,
          }}
        >
          Most brownfield projects link an existing GitHub repository. You can also create a new repo or skip for now.
        </p>
        <GitHubRepoSetupSection
          initialMode="link_existing"
          onChange={setGithubPayload}
          errorMsg={githubErrorMsg}
          errorNextAction={githubErrorNextAction}
          onRetry={clearGithubError}
          onModeChange={clearGithubError}
        />
        {githubLinkIncomplete && (
          <p
            data-testid="github-link-incomplete-hint"
            style={{
              fontFamily: 'var(--font-body)',
              fontSize:   11,
              color:      'var(--text-secondary)',
              marginTop:  -4,
              marginBottom: 12,
            }}
          >
            Enter the owner and repository name to link an existing GitHub repo, or pick a different mode.
          </p>
        )}
      </div>

      {/* MODE.B4 (#1177) — Domain Pack selector, rendered canonical-LAST
          (after the GitHub section, before the button row) per B5's
          canonical-ordering memo. Optional; default Generic. The selected
          pack is applied via PATCH /projects/:id/domain-pack after create
          (see goToReview), mirroring NewProjectModal:365-408 + 163-175.
          createProjectSchema has no domainPackId field, so the pack is NOT
          a POST-body field (no API change) — AC#3's "POST body" wording
          resolves to "selected pack applied to the project". */}
      <div style={{ marginTop: 32 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6 }}>
          <label
            htmlFor="domain-pack-select"
            style={{ fontFamily: 'var(--font-body)', fontSize: 13, fontWeight: 500, color: 'var(--text)' }}
          >
            Project Domain
          </label>
          <HelpPopover
            id="domain-pack-selection"
            variant="info"
            heading="Domain pack consequences"
            body={
              <>
                <p style={{ margin: '0 0 8px' }}>
                  Your project domain changes how agents work, not just how the project is labelled.
                </p>
                <p style={{ margin: '0 0 8px' }}>
                  Agents pick up the domain at next session start and adjust their prompts, gate criteria, and review checks accordingly. <strong>Healthcare</strong> adds HIPAA, NABH, and WCAG 2.1 AA criteria. <strong>BFSI</strong> adds RBI and SEBI cyber-resilience criteria. <strong>Generic</strong> uses no industry-specific overlay.
                </p>
                <p style={{ margin: 0 }}>
                  Pick the domain that matches your project&apos;s regulatory context. If your project genuinely doesn&apos;t have one, Generic is correct — choosing a regulated domain just to be safe adds gate friction without compliance benefit.
                </p>
              </>
            }
          />
        </div>
        <select
          id="domain-pack-select"
          data-testid="domain-pack-select"
          value={domainPack}
          onChange={e => setDomainPack(e.target.value as DomainPackValue)}
          style={selectStyle}
          onFocus={e => { e.currentTarget.style.borderColor = 'var(--primary)'; e.currentTarget.style.boxShadow = '0 0 0 3px rgba(124,58,237,0.12)'; }}
          onBlur={e => { e.currentTarget.style.borderColor = 'var(--border)'; e.currentTarget.style.boxShadow = 'none'; }}
        >
          {DOMAIN_PACKS.map(p => (
            <option key={p.value} value={p.value}>{p.label}</option>
          ))}
        </select>
        <p style={{ fontFamily: 'var(--font-body)', fontSize: 11, color: 'var(--text-secondary)', marginTop: 6 }}>
          Adds regulatory context to every agent session for this project. Change anytime in project settings.
        </p>
      </div>

      <div className="flex justify-between mt-8">
        <button
          type="button"
          onClick={() => {
            // R3 MEDIUM-2 — Section unmounts on step !== 2. Reset
            // parent payload so it stays in lock-step with the
            // section's fresh-on-remount field state.
            resetGithubPayload();
            clearGithubError();
            setStep(hasPriorPhases ? 1 : 0);
          }}
          style={{
            padding: '10px 24px',
            fontFamily: 'var(--font-body)',
            fontSize: 14,
            fontWeight: 500,
            color: 'var(--text-secondary)',
            background: 'none',
            border: '1px solid var(--border)',
            borderRadius: 'var(--radius-sm)',
            cursor: 'pointer',
          }}
        >
          Back
        </button>
        <button
          type="button"
          disabled={files.length === 0 || loading || githubLinkIncomplete}
          onClick={goToReview}
          style={{
            padding: '10px 24px',
            fontFamily: 'var(--font-display)',
            fontSize: 14,
            fontWeight: 600,
            color: 'white',
            background: 'var(--primary)',
            border: 'none',
            borderRadius: 'var(--radius-sm)',
            cursor: (files.length === 0 || loading || githubLinkIncomplete) ? 'not-allowed' : 'pointer',
            opacity: (files.length === 0 || loading || githubLinkIncomplete) ? 0.5 : 1,
          }}
        >
          {loading ? 'Uploading & Classifying...' : 'Upload & Classify'}
        </button>
      </div>
    </div>
    );
  };

  const renderStep3 = () => (
    <div style={{ maxWidth: 800, margin: '0 auto' }}>
      <h2 style={{ fontFamily: 'var(--font-display)', fontSize: 20, fontWeight: 700, color: 'var(--text)', marginBottom: 4 }}>
        Review Classifications
      </h2>
      <p style={{ fontFamily: 'var(--font-body)', fontSize: 14, color: 'var(--text-secondary)', marginBottom: 8 }}>
        We classified your documents automatically. Review the classifications below.
      </p>

      {summary && (
        <div className="flex gap-4 mb-6" style={{ flexWrap: 'wrap' }}>
          <SummaryBadge label="Total" value={summary.total} bg="var(--surface-alt)" color="var(--text)" />
          <SummaryBadge label="Imported" value={summary.imported} bg="rgba(16,185,129,0.12)" color="var(--success)" />
          <SummaryBadge label="Needs Review" value={summary.needsReview} bg="rgba(245,158,11,0.12)" color="var(--warning)" />
          {summary.failed > 0 && (
            <SummaryBadge label="Failed" value={summary.failed} bg="rgba(239,68,68,0.10)" color="var(--error)" />
          )}
        </div>
      )}

      {/* Waiver summary */}
      {priorPhases.filter(p => p.action === 'waive').length > 0 && (
        <div style={{
          background: 'rgba(245,158,11,0.06)',
          border: '1px solid rgba(245,158,11,0.2)',
          borderRadius: 'var(--radius-sm)',
          padding: '10px 14px',
          marginBottom: 16,
        }}>
          <p style={{ fontFamily: 'var(--font-body)', fontSize: 12, fontWeight: 600, color: 'var(--warning)', marginBottom: 4 }}>
            Waived Phases
          </p>
          {priorPhases.filter(p => p.action === 'waive').map(p => (
            <p key={p.phase} style={{ fontFamily: 'var(--font-body)', fontSize: 12, color: 'var(--text-secondary)' }}>
              {p.label}: {p.waiverReason}
            </p>
          ))}
        </div>
      )}

      <div
        style={{
          border: '1px solid var(--border)',
          borderRadius: 'var(--radius-sm)',
          overflow: 'hidden',
        }}
      >
        <table style={{ width: '100%', borderCollapse: 'collapse', fontFamily: 'var(--font-body)', fontSize: 13 }}>
          <thead>
            <tr style={{ background: 'var(--surface-alt)', borderBottom: '1px solid var(--border)' }}>
              <th style={thStyle}>File</th>
              <th style={thStyle}>Type</th>
              <th style={thStyle}>Phase</th>
              <th style={{ ...thStyle, textAlign: 'center' }}>Confidence</th>
              <th style={{ ...thStyle, textAlign: 'center' }}>Status</th>
            </tr>
          </thead>
          <tbody>
            {results.map((r, i) => {
              const conf = confidenceColor(r.confidence);
              return (
                <tr key={i} style={{ borderBottom: i < results.length - 1 ? '1px solid var(--border)' : 'none' }}>
                  <td style={tdStyle}>
                    <span style={{ fontWeight: 500, color: 'var(--text)' }}>{r.filename}</span>
                    {r.status === 'FAILED' && r.reason && (
                      <div style={{ fontSize: 11, color: 'var(--error)', marginTop: 2 }}>{r.reason}</div>
                    )}
                  </td>
                  <td style={tdStyle}>
                    {r.status !== 'FAILED' ? (
                      <span style={{ fontSize: 12, color: 'var(--text)' }}>
                        {ARTEFACT_TYPES.find(t => t.value === r.artefactType)?.label ?? r.artefactType ?? '\u2014'}
                      </span>
                    ) : (
                      <span style={{ color: 'var(--text-secondary)' }}>{'\u2014'}</span>
                    )}
                  </td>
                  <td style={tdStyle}>
                    {r.status !== 'FAILED' ? (
                      <span style={{ fontSize: 12, color: 'var(--text)' }}>
                        {PHASES.find(p => p.value === r.phase)?.label ?? r.phase ?? '\u2014'}
                      </span>
                    ) : (
                      <span style={{ color: 'var(--text-secondary)' }}>{'\u2014'}</span>
                    )}
                  </td>
                  <td style={{ ...tdStyle, textAlign: 'center' }}>
                    <span
                      style={{
                        display: 'inline-block',
                        padding: '2px 8px',
                        borderRadius: 999,
                        fontSize: 11,
                        fontWeight: 600,
                        background: conf.bg,
                        color: conf.color,
                      }}
                    >
                      {r.confidence !== null ? `${Math.round(r.confidence * 100)}%` : '\u2014'}
                    </span>
                  </td>
                  <td style={{ ...tdStyle, textAlign: 'center' }}>
                    <StatusBadge status={r.status} />
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      <div className="flex justify-between mt-8">
        <button
          type="button"
          onClick={() => setStep(2)}
          style={{
            padding: '10px 24px',
            fontFamily: 'var(--font-body)',
            fontSize: 14,
            fontWeight: 500,
            color: 'var(--text-secondary)',
            background: 'none',
            border: '1px solid var(--border)',
            borderRadius: 'var(--radius-sm)',
            cursor: 'pointer',
          }}
        >
          Back
        </button>
        <button
          type="button"
          onClick={goToComplete}
          style={{
            padding: '10px 24px',
            fontFamily: 'var(--font-display)',
            fontSize: 14,
            fontWeight: 600,
            color: 'white',
            background: 'var(--primary)',
            border: 'none',
            borderRadius: 'var(--radius-sm)',
            cursor: 'pointer',
          }}
        >
          Confirm & Continue
        </button>
      </div>
    </div>
  );

  const renderStep4 = () => {
    const imported = results.filter(r => r.status !== 'FAILED').length;
    const failed = results.filter(r => r.status === 'FAILED').length;
    const waived = priorPhases.filter(p => p.action === 'waive').length;

    return (
      <div style={{ maxWidth: 480, margin: '0 auto', textAlign: 'center' }}>
        <div style={{ fontSize: 48, marginBottom: 16 }}>&#10003;</div>
        <h2 style={{ fontFamily: 'var(--font-display)', fontSize: 22, fontWeight: 700, color: 'var(--text)', marginBottom: 8 }}>
          Project Created
        </h2>
        <p style={{ fontFamily: 'var(--font-body)', fontSize: 14, color: 'var(--text-secondary)', marginBottom: 8 }}>
          <strong>{name}</strong> has been created with {imported} imported artefact{imported !== 1 ? 's' : ''}.
          {failed > 0 && <span style={{ color: 'var(--error)' }}> {failed} file{failed !== 1 ? 's' : ''} failed to import.</span>}
        </p>
        {waived > 0 && (
          <p style={{ fontFamily: 'var(--font-body)', fontSize: 13, color: 'var(--warning)', marginBottom: 8 }}>
            {waived} phase{waived !== 1 ? 's' : ''} waived.
          </p>
        )}
        <p style={{ fontFamily: 'var(--font-body)', fontSize: 13, color: 'var(--text-secondary)', marginBottom: 24 }}>
          Starting at {PHASES.find(p => p.value === startingPhase)?.label ?? startingPhase}.
        </p>

        <div className="flex justify-center gap-3">
          <button
            type="button"
            onClick={() => navigate('/projects')}
            style={{
              padding: '10px 24px',
              fontFamily: 'var(--font-body)',
              fontSize: 14,
              fontWeight: 500,
              color: 'var(--text-secondary)',
              background: 'none',
              border: '1px solid var(--border)',
              borderRadius: 'var(--radius-sm)',
              cursor: 'pointer',
            }}
          >
            All Projects
          </button>
          <button
            type="button"
            onClick={() => navigate(`/projects/${projectId}`)}
            style={{
              padding: '10px 24px',
              fontFamily: 'var(--font-display)',
              fontSize: 14,
              fontWeight: 600,
              color: 'white',
              background: 'var(--primary)',
              border: 'none',
              borderRadius: 'var(--radius-sm)',
              cursor: 'pointer',
            }}
          >
            Go to Project
          </button>
        </div>
      </div>
    );
  };

  // ── Main render ────────────────────────────────────────────────────────

  return (
    <div className="flex min-h-screen" style={{ background: 'var(--bg)' }}>
      <Sidebar />

      <main className="flex-1 overflow-auto">
        {/* Top bar */}
        <div
          className="px-8 py-5 border-b sticky top-0 z-10"
          style={{ background: 'var(--surface)', borderColor: 'var(--border)' }}
        >
          <h1 style={{ fontFamily: 'var(--font-display)', fontSize: 24, fontWeight: 700, color: 'var(--text)' }}>
            Onboard Existing Project
          </h1>
          <p style={{ fontFamily: 'var(--font-body)', fontSize: 14, color: 'var(--text-secondary)', marginTop: 2 }}>
            Import documents from an existing project into BluePrint.
          </p>
        </div>

        {/* Wizard card */}
        <div className="px-8 py-8" style={{ maxWidth: 900, margin: '0 auto' }}>
          <div
            className="rounded-xl border shadow-sm"
            style={{
              background: 'var(--surface)',
              borderColor: 'var(--border)',
              padding: 'var(--space-6)',
            }}
          >
            {renderStepper()}

            {error && (
              <div style={{
                fontFamily: 'var(--font-body)',
                fontSize: 13,
                color: 'var(--error)',
                background: 'rgba(239,68,68,0.06)',
                border: '1px solid rgba(239,68,68,0.2)',
                borderRadius: 'var(--radius-sm)',
                padding: '8px 12px',
                marginBottom: 16,
                maxWidth: 560,
                margin: '0 auto 16px',
              }}>
                {error}
              </div>
            )}

            {step === 0 && renderStep0()}
            {step === 1 && renderStep1()}
            {step === 2 && renderStep2()}
            {step === 3 && renderStep3()}
            {step === 4 && renderStep4()}
          </div>
        </div>
      </main>
    </div>
  );
}

// ── Sub-components ─────────────────────────────────────────────────────────

const thStyle: React.CSSProperties = {
  padding: '10px 12px',
  textAlign: 'left',
  fontWeight: 600,
  color: 'var(--text-secondary)',
  fontSize: 12,
  textTransform: 'uppercase',
  letterSpacing: '0.04em',
};

const tdStyle: React.CSSProperties = {
  padding: '10px 12px',
  verticalAlign: 'middle',
};

function SummaryBadge({ label, value, bg, color }: { label: string; value: number; bg: string; color: string }) {
  return (
    <div
      style={{
        padding: '6px 14px',
        borderRadius: 'var(--radius-sm)',
        background: bg,
        display: 'flex',
        alignItems: 'center',
        gap: 6,
      }}
    >
      <span style={{ fontFamily: 'var(--font-display)', fontSize: 18, fontWeight: 700, color }}>{value}</span>
      <span style={{ fontFamily: 'var(--font-body)', fontSize: 12, color: 'var(--text-secondary)' }}>{label}</span>
    </div>
  );
}

function StatusBadge({ status }: { status: string }) {
  const map: Record<string, { bg: string; color: string; label: string }> = {
    IMPORTED: { bg: 'rgba(16,185,129,0.12)', color: 'var(--success)', label: 'Imported' },
    NEEDS_REVIEW: { bg: 'rgba(245,158,11,0.12)', color: 'var(--warning)', label: 'Needs Review' },
    FAILED: { bg: 'rgba(239,68,68,0.10)', color: 'var(--error)', label: 'Failed' },
  };
  const s = map[status] ?? { bg: 'var(--surface-alt)', color: 'var(--text-secondary)', label: status };
  return (
    <span
      style={{
        display: 'inline-block',
        padding: '2px 8px',
        borderRadius: 999,
        fontSize: 11,
        fontWeight: 600,
        background: s.bg,
        color: s.color,
      }}
    >
      {s.label}
    </span>
  );
}
